import { useEffect, useRef } from "react"

import useHasAccess from "@hooks/useHasAccess"
import useUser from "@hooks/useUser"

import { faIcon } from "@utils/faIcon"

import EmptyState from "@molecules/EmptyState/EmptyState"

import { Settings } from "@templates/index"

import { PERMISSION_LEVEL } from "@constants/permissionLevel"

import { AUTHORIZED_CONNECTION_PARAM_NAME } from "./SettingsCRM.constants"
import styles from "./SettingsCRM.module.scss"
import { useCRMConfigQuery, useCRMConnectionQuery, useCRMRecordConnectionMutation } from "./SettingsCRM.store"
import CRMConnectionCard from "./components/CRMConnectionCard/CRMConnectionCard"
import CRMSyncSettingsSection from "./components/CRMSyncSettingsSection/CRMSyncSettingsSection"

function SettingsCRMContent() {
    const connectionQuery = useCRMConnectionQuery()
    const configQuery = useCRMConfigQuery()

    const { user } = useUser()
    const { hasPermission } = useHasAccess()
    const recordConnectionMutation = useCRMRecordConnectionMutation()

    // Record the connection Vault just authorized once the user has loaded, then strip its param from the URL.
    const hasHandledAuthorizationReturnRef = useRef(false)
    useEffect(() => {
        if (!user || hasHandledAuthorizationReturnRef.current) {
            return
        }
        hasHandledAuthorizationReturnRef.current = true

        const searchParams = new URLSearchParams(window.location.search)
        if (!searchParams.has(AUTHORIZED_CONNECTION_PARAM_NAME)) {
            return
        }

        if (hasPermission("settings_edit_permission", PERMISSION_LEVEL.FULL)) {
            recordConnectionMutation.mutate()
        }

        searchParams.delete(AUTHORIZED_CONNECTION_PARAM_NAME)
        const remainingQuery = searchParams.toString()
        window.history.replaceState(null, "", window.location.pathname + (remainingQuery ? `?${remainingQuery}` : ""))
    }, [user])

    const connection = connectionQuery.data

    // The sync settings follow the integration on record, which outlives a broken connection.
    const isConnectionOnRecord = !!connection?.crm_integration_id

    const renderConnectionSettings = () => {
        // A failed read says nothing about the connection, so it must not render as "Not connected".
        if (connectionQuery.isError || configQuery.isError) {
            return (
                <Settings.Section>
                    <div className={styles.emptyStateContainer}>
                        <EmptyState
                            variant="error"
                            icon={faIcon({ name: "circle-xmark", style: "solid" })}
                            title="Hmm, something went wrong..."
                            supportText="An error occurred while fetching your CRM settings."
                            primaryCTA={{
                                children: "Try again",
                                onClick: () => {
                                    void connectionQuery.refetch()
                                    void configQuery.refetch()
                                },
                            }}
                        />
                    </div>
                </Settings.Section>
            )
        }

        return (
            <>
                <Settings.Section>
                    <Settings.PageHeader
                        variant="section-heading"
                        title="Connection"
                        subtitle="Connect Roopairs with your CRM."
                    />
                    <CRMConnectionCard
                        connection={connection}
                        isLoading={connectionQuery.isLoading || recordConnectionMutation.isPending}
                    />
                </Settings.Section>

                {isConnectionOnRecord && (
                    <>
                        <Settings.Divider />

                        <Settings.Section>
                            <Settings.PageHeader
                                variant="section-heading"
                                title="Sync settings"
                                subtitle={`Choose which Roopairs records are exported to ${connection.crm_integration_name}.`}
                            />
                            <CRMSyncSettingsSection />
                        </Settings.Section>
                    </>
                )}
            </>
        )
    }

    return (
        <Settings.PageContainer>
            <Settings.PageHeader
                variant="page-heading"
                title="CRM"
                subtitle="Sync clients, service locations and contacts with your CRM."
            />

            <Settings.Divider />

            {renderConnectionSettings()}
        </Settings.PageContainer>
    )
}

export default function SettingsCRM() {
    return (
        <Settings.Wrapper title="CRM">
            <SettingsCRMContent />
        </Settings.Wrapper>
    )
}
